import {
  addMonths,
  differenceInMonths,
  endOfMonth,
  startOfMonth,
  subMonths,
} from "date-fns";
import { useAtomValue } from "jotai";
import { usePage } from "@inertiajs/inertia-react";
import { useMemo } from "react";
import { dateFilterAtom, generalFilterAtom } from "@/Stores";
import EmissionGraphPerScope from "@/Components/Dashboard/EmissionGraphPerScope";
import { dateToIndex } from "@/Utilities/Utilities";
import EmissionGraphPerTeams from "@/Components/Dashboard/EmissionGraphPerTeams";

const DashboardGraphs = () => {
  const questions = usePage().props?.questions?.data || [];
  const answers = usePage().props?.answers || [];
  const teams = usePage().props?.teams?.data || [];

  const generalFilter = useAtomValue(generalFilterAtom);
  const dateFilter = useAtomValue(dateFilterAtom);

  const currentDate = new Date();

  const startDate = new Date(
    dateFilter?.from || startOfMonth(subMonths(currentDate, 12)),
  );
  const endDate = new Date(dateFilter?.to || endOfMonth(currentDate));

  const monthDiff = differenceInMonths(endDate, startDate) + 1;
  const data = [];

  let lastYear = null;

  for (let i = 0; i < monthDiff; i++) {
    const month = addMonths(startDate, i);
    const year = month.getFullYear();
    const monthName = month.toLocaleString("en-US", { month: "short" });
    let name = `${monthName}`;

    if (year !== lastYear) {
      name = `${year} ${monthName}`;
      lastYear = year;
    }

    data.push({
      key: dateToIndex(month),
      name: name,
    });
  }

  const labels = data;

  // Preprocess teams and questions for direct access
  const teamMap = new Map(teams.map((team) => [team.id, team.name]));
  const questionMap = new Map(questions.map((q) => [q.id, q.title]));

  const entries = {
    teams: {},
    questions: {},
  };

  Object.entries(answers).forEach(([question_id, answer]) => {
    if (!answer.has_emission) return;

    // Handling questions
    if (!entries.questions.hasOwnProperty(question_id)) {
      entries.questions[question_id] = {
        label: questionMap.get(Number(question_id)),
        data: {},
        scientific_uncertainty: answer.scientific_uncertainty,
      };
    }

    const questionData = entries.questions[question_id].data;

    Object.entries(answer.teams).forEach(([team_id, team_info]) => {
      // Handling teams
      if (!entries.teams.hasOwnProperty(team_id)) {
        entries.teams[team_id] = {
          label: teamMap.get(Number(team_id)),
          data: {},
        };
      }

      const teamData = entries.teams[team_id].data;

      // Proportional emissions
      Object.entries(team_info.proportional_emission).forEach(
        ([key, value]) => {
          questionData[key] = (questionData[key] || 0) + value;
        },
      );

      // Monthly emissions
      Object.entries(team_info.monthly_emission).forEach(
        ([year_month, emission]) => {
          teamData[year_month] = teamData[year_month] || {};

          Object.entries(emission).forEach(([key, value]) => {
            teamData[year_month][key] =
              (teamData[year_month][key] || 0) + value;
          });
        },
      );
    });
  });

  return (
    <>
      <EmissionGraphPerScope labels={labels} entries={entries} />
      <EmissionGraphPerTeams labels={labels} entries={entries} />
    </>
  );
};

export default DashboardGraphs;
